import { useEffect, useState } from "react";
import { ClipboardCopy, ClipboardPaste, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { copyOrWarn } from "@/lib/api";

export const ClipboardPanel = ({ send, subscribe, live }) => {
  const [remote, setRemote] = useState("");
  const [local, setLocal] = useState("");

  useEffect(() => subscribe((m) => {
    if (m.type !== "clipboard") return;
    setRemote(m.text);
    navigator.clipboard?.writeText(m.text)
      .then(() => toast.success("Texte copié depuis le poste distant"))
      .catch(() => toast("Texte copié sur le poste distant", { description: "Ouvrez « Presse-papiers » pour le récupérer." }));
  }), [subscribe]);

  const copyRemote = () => copyOrWarn(remote, "Copié sur votre ordinateur");
  const pasteLocal = async () => {
    try { setLocal(await navigator.clipboard.readText()); } catch { toast.error("Collez le texte manuellement dans la zone"); }
  };
  const push = (paste) => {
    send({ type: "clipboard_set", text: local, paste });
    toast.success(paste ? "Texte collé sur le poste distant" : "Envoyé dans le presse-papiers distant");
  };

  return (
    <aside className="w-80 border-l border-slate-800 bg-[#0B1120] flex flex-col p-4 gap-5 text-sm" data-testid="clipboard-panel">
      <p className="text-xs text-slate-400">Ctrl/Cmd + C et Ctrl/Cmd + V fonctionnent directement dans l'écran distant. Utilisez ce panneau si besoin.</p>
      <section className="space-y-2">
        <p className="font-medium text-slate-200">Copié sur le poste distant</p>
        <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-slate-900 border border-slate-800 p-2 text-xs text-slate-300 min-h-[3rem]" data-testid="clipboard-remote-text">{remote || "Rien pour l'instant."}</pre>
        <Button size="sm" variant="secondary" className="w-full" disabled={!remote} onClick={copyRemote} data-testid="clipboard-copy-remote-btn"><ClipboardCopy className="h-4 w-4 mr-1.5" /> Copier sur mon ordinateur</Button>
      </section>
      <section className="space-y-2">
        <p className="font-medium text-slate-200">Envoyer vers le poste distant</p>
        <Textarea value={local} onChange={(e) => setLocal(e.target.value)} placeholder="Collez ou tapez du texte…" className="h-28 bg-slate-900 border-slate-800 text-slate-200" data-testid="clipboard-local-input" />
        <Button size="sm" variant="ghost" className="w-full" onClick={pasteLocal} data-testid="clipboard-read-local-btn"><ClipboardPaste className="h-4 w-4 mr-1.5" /> Reprendre mon presse-papiers</Button>
        <Button size="sm" className="w-full" disabled={!live || !local} onClick={() => push(true)} data-testid="clipboard-paste-remote-btn"><Send className="h-4 w-4 mr-1.5" /> Coller sur le poste distant</Button>
        <Button size="sm" variant="secondary" className="w-full" disabled={!live || !local} onClick={() => push(false)} data-testid="clipboard-set-remote-btn">Mettre dans son presse-papiers</Button>
      </section>
    </aside>
  );
};
